import { all } from '../../shared/lib/functional';

/**
 * The `Filter` class defines a set of operations to act on "filterable" items,
 * keeps track of the applied filters, and ensures the filters' state is
 * consistent at all times.
 *
 * A new filter is created by instantiating `Filter` and passing an array of
 * `Filterable` elements along with (optionally) a set of filter
 * categories. Filter categories define additional filters than apply a state
 * based on group criteria (e.g., select all ingredients with type ===
 * 'vegetable')
 *
 * `FilterOption`s are applied filters by item, by category, or by "all". They
 * can be retrieved by the UI to display the current state, and can be passed
 * via `updateFilter` to change what is selected.
 *
 */
export interface FilterOption {
  id: string;
  label: string;
  selected: boolean;
  type: 'item' | 'category' | 'all';
}

export interface ItemFilterOption<T> extends FilterOption {
  item: T;
}

export interface CategoryFilterOption extends FilterOption {
  categoryName: string;
}

export interface Filterable {
  _id: string;
  name: string;
}

export interface FilterCategory<T> {
  name: string;
  mapping: (i: T) => Filterable[];
}

export default class Filter<T extends Filterable> {
  private items: T[];
  private categories: Array<FilterCategory<T>>;

  private itemFilters: Array<ItemFilterOption<T>>;
  private categoryFilters: CategoryFilterOption[];
  private allFilter: FilterOption;

  private itemsByCategory: { [categoryName: string]: { [id: string]: Array<ItemFilterOption<T>> } };

  /**
   * Builds the internal state for a new filter, using category mappings if passed
   *
   */
  constructor(items: T[], categories: Array<FilterCategory<T>> = []) {
    this.items = items;
    this.categories = categories;

    this.itemFilters = this.buildItemFilters();
    this.itemsByCategory = this.buildCategoryMap(this.itemFilters);
    this.allFilter = { id: 'all', label: 'All', selected: false, type: 'all' };

    this.categoryFilters = this.buildCategoryFilters();
  }

  /**
   * Return a list of items based on the currently applied filter options
   *
   */
  public getSelectedItems(): T[] {
    let selectedIds = this.itemFilters.filter(f => f.selected).map(f => f.id);
    return this.items.filter(i => selectedIds.find(id => id === i._id));
  }

  public getAllItems(): T[] {
    return this.items;
  }

  public getItemFilters() {
    return this.itemFilters;
  }

  public getCategoryFilters() {
    return this.categoryFilters;
  }

  public getAllFilter() {
    return this.allFilter;
  }

  public getFilters(): FilterOption[] {
    return (this.categoryFilters as FilterOption[])
      .concat(this.itemFilters)
      .concat([this.allFilter]);
  }

  public getFiltersById(id: string[]): FilterOption[] {
    const set = new Set(id);
    const allFilters = this.getFilters();
    return allFilters.filter(f => set.has(f.id));
  }

  public updateFilter(f: FilterOption) {
    if (f.type === 'category') {
      let category = this.categoryFilters.find(c => c.id === f.id);
      if (!category) {
        console.error(`Could not find category for ${f}`);
        return;
      }
      category.selected = f.selected;

      // update items in this category
      this.itemsByCategory[category.categoryName][f.id].forEach(itemFilter => {
        itemFilter.selected = f.selected;
      });

      this.updateCategoriesFromItems();
      this.updateAllFromItems();
    } else if (f.type === 'item') {
      let filter = this.itemFilters.find(fi => fi.id === f.id);
      if (!filter) {
        console.error(`Could not find item filter ${f}`);
        return;
      }
      filter.selected = f.selected;
      this.updateCategoriesFromItems();
      this.updateAllFromItems();
    } else if (f.type === 'all') {
      this.itemFilters.forEach(fi => (fi.selected = f.selected));
      this.categoryFilters.forEach(fc => (fc.selected = f.selected));
      this.allFilter.selected = f.selected;
    }
  }

  /**
   * Create a map of categories to filters
   */
  private buildCategoryMap(itemFilters: Array<ItemFilterOption<T>>): {
    [categoryName: string]: { [id: string]: Array<ItemFilterOption<T>> };
  } {
    let allMaps: { [categoryName: string]: { [id: string]: Array<ItemFilterOption<T>> } } = {};

    this.categories.forEach(categoryFilter => {
      let categoryMap: { [key: string]: Array<ItemFilterOption<T>> } = {};

      itemFilters.forEach(itemFilter => {
        let itemCategories = categoryFilter.mapping(itemFilter.item);

        itemCategories.forEach(itemCategory => {
          if (!(itemCategory._id in categoryMap)) {
            categoryMap[itemCategory._id] = [];
          }

          categoryMap[itemCategory._id].push(itemFilter);
        });
      });

      allMaps[categoryFilter.name] = categoryMap;
    });

    return allMaps;
  }

  private buildItemFilters(): Array<ItemFilterOption<T>> {
    return this.items.map<ItemFilterOption<T>>(item => {
      return {
        id: item._id,
        label: item.name,
        selected: false,
        type: 'item',
        item,
      };
    });
  }

  private buildCategoryFilters(): CategoryFilterOption[] {
    let allCategoryFilters: CategoryFilterOption[] = [];

    this.categories.forEach(category => {
      let categoryFilters: CategoryFilterOption[] = [];
      let seenCategories: { [id: string]: boolean } = {};

      this.items.forEach(item => {
        let categoryName = category.name;
        let itemCategories = category.mapping(item);

        itemCategories.forEach(itemCategory => {
          if (!seenCategories[itemCategory._id]) {
            seenCategories[itemCategory._id] = true;
            categoryFilters.push({
              id: itemCategory._id,
              label: itemCategory.name,
              selected: false,
              type: 'category',
              categoryName,
            });
          }
        });
      });

      allCategoryFilters = allCategoryFilters.concat(categoryFilters);
    });

    return allCategoryFilters;
  }

  // Update categories to reflect item filters' state
  private updateCategoriesFromItems() {
    this.categories.forEach(category => {
      let categoryMap = this.itemsByCategory[category.name];

      Object.keys(categoryMap).forEach(categoryId => {
        // A category is selected if all items in it are selected
        let selected = all(categoryMap[categoryId], i => i.selected);
        let categoryFilter = this.categoryFilters.find(c => c.id === categoryId);
        if (!categoryFilter) {
          console.error(`No Category found ${categoryId}`);
          return;
        }
        categoryFilter.selected = selected;
      });
    });
  }

  private updateAllFromItems() {
    this.allFilter.selected = all(this.itemFilters, i => i.selected);
  }
}
